import { useState, useEffect } from 'react'
import { Card } from '@radix-ui/themes'
import { useTranslation } from 'react-i18next'
import { ethers } from 'ethers'
import {
  ArrowUpRight,
  ArrowDownLeft,
  Copy,
  Check,
  Loader2,
  SquareArrowOutUpRight,
  X
} from 'lucide-react'
import { Transaction } from '@/types/transaction.types'
import { copyToClipboard } from '@/utils/clipboard'
import { getNetwork, getTxExplorerUrl } from '@/config/networks'
import { useTransactions } from '@/hooks/useTransactions'

interface TransactionDetailDialogProps {
  open: boolean
  onClose: () => void
  tx: Transaction | null
}

const CopyButton = ({ value }: { value: string }) => {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    const success = await copyToClipboard(value)
    if (success) {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  return (
    <button
      onClick={handleCopy}
      className="flex-shrink-0 p-1 rounded text-text-secondary hover:text-primary transition-colors"
    >
      {copied ? (
        <Check className="w-3.5 h-3.5 text-success" />
      ) : (
        <Copy className="w-3.5 h-3.5" />
      )}
    </button>
  )
}

const DetailRow = ({
  label,
  children
}: {
  label: string
  children: React.ReactNode
}) => (
  <div className="flex justify-between items-center gap-4 py-3 border-b border-border-base last:border-0">
    <p className="text-xs text-text-secondary uppercase tracking-widest font-bold shrink-0">
      {label}
    </p>
    <div className="flex items-center gap-1 min-w-0">{children}</div>
  </div>
)

const TransactionDetailDialog = ({
  open,
  onClose,
  tx
}: TransactionDetailDialogProps) => {
  const { t } = useTranslation()
  const { getExtendedTransactionDetails } = useTransactions()
  const [liveDetails, setLiveDetails] = useState<Partial<Transaction>>({})
  const [loadingDetails, setLoadingDetails] = useState(true)

  useEffect(() => {
    if (!open || !tx) {
      setLiveDetails({})
      setLoadingDetails(true)
      return
    }
    let cancelled = false
    setLoadingDetails(true)
    getExtendedTransactionDetails(tx.hash, tx.network).then((details) => {
      if (cancelled) return
      setLiveDetails(details)
      setLoadingDetails(false)
    })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tx?.hash, tx?.network])

  if (!tx) return null

  const isSend = tx.type === 'send'
  const nativeSymbol = getNetwork(tx.network)?.nativeCurrency?.symbol || 'ETH'
  const coin = tx.tokenSymbol || nativeSymbol
  const explorerUrl = getTxExplorerUrl(tx.network, tx.hash)

  const nonce = liveDetails.nonce ?? tx.nonce
  const gasLimit = liveDetails.gasLimit ?? tx.gasLimit
  const gasUsed = liveDetails.gasUsed ?? tx.gasUsed
  const gasPriceWei = liveDetails.gasPrice ?? tx.gasPrice
  const gasPriceGwei = gasPriceWei
    ? Number(ethers.formatUnits(gasPriceWei, 'gwei'))
    : undefined

  return (
    <div
      className="fixed inset-0 bg-opacity-50 bg-black/50 backdrop-blur-sm overflow-y-auto h-full w-full z-50"
      style={{ display: open ? 'block' : 'none' }}
    >
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <Card className="shadow-xl bg-card w-full max-w-md justify-center relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-text-secondary z-10"
            aria-label="Close dialog"
            type="button"
          >
            <X className="w-6 h-6" />
          </button>

          <div className="flex flex-col gap-6 p-4">
            <div className="flex flex-col gap-3 items-center text-center">
              <div
                className={`w-14 h-14 rounded-full flex items-center justify-center ${isSend ? 'bg-accent/10' : 'bg-success/10'}`}
              >
                {isSend ? (
                  <ArrowUpRight className="text-accent w-6 h-6" />
                ) : (
                  <ArrowDownLeft className="text-success w-6 h-6" />
                )}
              </div>
              <h2 className="text-text-main text-2xl font-bold">
                {(isSend ? '-' : '+') +
                  Number(tx.value || '0').toLocaleString(undefined, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 8
                  })}{' '}
                {coin}
              </h2>
            </div>

            {loadingDetails ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="w-6 h-6 animate-spin text-text-secondary" />
              </div>
            ) : (
              <>
                <div className="flex flex-col">
                  <DetailRow label={t('dialogs.tx_from')}>
                    <span className="text-text-main text-sm break-all text-right">
                      {tx.from}
                    </span>
                    <CopyButton value={tx.from} />
                  </DetailRow>

                  <DetailRow label={t('dialogs.tx_to')}>
                    <span className="text-text-main text-sm break-all text-right">
                      {tx.to}
                    </span>
                    <CopyButton value={tx.to} />
                  </DetailRow>

                  <DetailRow label={t('dialogs.tx_time')}>
                    <span className="text-text-main text-sm">
                      {new Date(tx.timestamp).toLocaleString()}
                    </span>
                  </DetailRow>

                  <DetailRow label={t('dialogs.tx_block')}>
                    <span className="text-text-main text-sm">{tx.blockNumber}</span>
                  </DetailRow>

                  {nonce !== undefined && (
                    <DetailRow label={t('dialogs.tx_nonce')}>
                      <span className="text-text-main text-sm">{nonce}</span>
                    </DetailRow>
                  )}

                  {gasLimit && (
                    <DetailRow label={t('dialogs.tx_gas_limit')}>
                      <span className="text-text-main text-sm">{gasLimit}</span>
                    </DetailRow>
                  )}

                  {gasUsed && (
                    <DetailRow label={t('dialogs.tx_gas_used')}>
                      <span className="text-text-main text-sm">{gasUsed}</span>
                    </DetailRow>
                  )}

                  {gasPriceGwei !== undefined && (
                    <DetailRow label={t('dialogs.tx_gas_price')}>
                      <span className="text-text-main text-sm">{gasPriceGwei} GWEI</span>
                    </DetailRow>
                  )}

                  <DetailRow label={t('dialogs.tx_hash')}>
                    <span className="text-text-main text-sm break-all text-right">
                      {tx.hash}
                    </span>
                    <CopyButton value={tx.hash} />
                  </DetailRow>
                </div>

                {explorerUrl && (
                  <a
                    href={explorerUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-gradient-fill flex items-center justify-center gap-2 rounded-full py-3.5"
                  >
                    <p className="font-bold">{t('dialogs.tx_view_explorer')}</p>
                    <SquareArrowOutUpRight className="w-4 h-4" />
                  </a>
                )}
              </>
            )}
          </div>
        </Card>
      </div>
    </div>
  )
}

export default TransactionDetailDialog
